import * as React from 'react'
import { useQuery } from '@tanstack/react-query'
import { ComputerIcon } from 'lucide-react'

import { type AppFilterOption, getFilterOptions } from '@/api/teamInsights'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Command, CommandInput, CommandList } from '@/components/ui/command'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { cn } from '@/lib/utils'

interface AppFilterProps {
  enterpriseId: string | null
  appKeys: string[]
  appCategories: string[]
  onChange: (next: { appKeys: string[]; appCategories: string[] }) => void
  disabled?: boolean
  // Applied to the trigger Button — lets parents stretch the pill to fill a grid cell.
  triggerClassName?: string
}

const MAX_APPS_PER_CATEGORY = 20

// Fixed display order + labels (mirrors backend contract).
const CATEGORY_ORDER: { key: string; label: string }[] = [
  { key: 'personal_messaging', label: 'Personal messages' },
  { key: 'work', label: 'Work messages' },
  { key: 'email', label: 'Emails' },
  { key: 'ai', label: 'AI prompts' },
  { key: 'document', label: 'Documents' },
  { key: 'other', label: 'Other' },
]

const toggle = (arr: string[], value: string): string[] =>
  arr.includes(value) ? arr.filter((v) => v !== value) : [...arr, value]

const AppFilter = ({
  enterpriseId,
  appKeys,
  appCategories,
  onChange,
  disabled = false,
  triggerClassName,
}: AppFilterProps) => {
  const [open, setOpen] = React.useState(false)
  const [search, setSearch] = React.useState('')

  const filterOptions = useQuery({
    queryKey: ['team-insights', 'filter-options', enterpriseId],
    queryFn: () => getFilterOptions(enterpriseId!).then((r) => r.data),
    enabled: open && !!enterpriseId,
    staleTime: 5 * 60 * 1000,
  })

  const apps = React.useMemo(() => filterOptions.data?.apps ?? [], [filterOptions.data?.apps])

  // Bucket apps by category
  const appsByCategory = React.useMemo(() => {
    const map = new Map<string, AppFilterOption[]>()
    for (const app of apps) {
      const list = map.get(app.category) ?? []
      list.push(app)
      map.set(app.category, list)
    }
    return map
  }, [apps])

  const searchLower = search.trim().toLowerCase()
  const isSearching = searchLower.length > 0
  const matchesSearch = (app: AppFilterOption) =>
    !searchLower ||
    app.display_name.toLowerCase().includes(searchLower) ||
    app.app_key.toLowerCase().includes(searchLower)

  const totalSelected = appKeys.length + appCategories.length

  const triggerLabel = React.useMemo(() => {
    if (totalSelected === 0) return 'All apps'

    // Resolve a display name for the first selected item.
    const firstCategoryKey = appCategories[0]
    const firstAppKey = appKeys[0]
    let firstName = ''
    if (firstCategoryKey) {
      firstName = CATEGORY_ORDER.find((c) => c.key === firstCategoryKey)?.label ?? firstCategoryKey
    } else if (firstAppKey) {
      firstName = apps.find((a) => a.app_key === firstAppKey)?.display_name ?? firstAppKey
    }
    if (totalSelected === 1) return firstName
    return `${firstName} +${totalSelected - 1}`
  }, [appCategories, appKeys, apps, totalSelected])

  const clearAll = () => onChange({ appKeys: [], appCategories: [] })

  const toggleCategory = (categoryKey: string) =>
    onChange({ appKeys, appCategories: toggle(appCategories, categoryKey) })

  const toggleApp = (appKey: string) => onChange({ appKeys: toggle(appKeys, appKey), appCategories })

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        if (!next) {
          setOpen(false)
          return
        }
        if (!disabled) setOpen(true)
      }}
    >
      <PopoverTrigger asChild>
        <Button variant="outline" size="lg" disabled={disabled} className={cn(triggerClassName)}>
          <ComputerIcon className="size-[22px]" />
          <span className={cn('truncate text-left', triggerClassName ? 'flex-1' : 'max-w-[160px]')}>
            {triggerLabel}
          </span>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[320px] p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput placeholder="Search by desktop app name" value={search} onValueChange={setSearch} />
          {!filterOptions.isLoading && !filterOptions.isError && (
            <>
              <button
                type="button"
                onClick={clearAll}
                className="hover:bg-accent flex w-full items-center px-3 py-2.5 text-left text-sm font-semibold"
              >
                Clear filter
              </button>
              <div className="bg-border mx-3 h-px" />
            </>
          )}
          <CommandList className="max-h-[420px]">
            {filterOptions.isLoading && <div className="text-muted-foreground py-6 text-center text-sm">Loading…</div>}
            {filterOptions.isError && (
              <div className="text-muted-foreground py-6 text-center text-sm">Couldn’t load app options.</div>
            )}
            {!filterOptions.isLoading && !filterOptions.isError && (
              <div className="py-1">
                {isSearching && apps.filter(matchesSearch).length === 0 && (
                  <div className="text-muted-foreground py-6 text-center text-sm">No apps match “{search.trim()}”</div>
                )}
                {CATEGORY_ORDER.map(({ key: categoryKey, label }) => {
                  const categoryChecked = appCategories.includes(categoryKey)
                  const categoryApps = appsByCategory.get(categoryKey) ?? []
                  const matchingApps = categoryApps.filter(matchesSearch)
                  const truncatedApps = matchingApps.slice(0, MAX_APPS_PER_CATEGORY)
                  const hasSelectedHiddenApp = matchingApps
                    .slice(MAX_APPS_PER_CATEGORY)
                    .some((a) => appKeys.includes(a.app_key))
                  // If a selected app would otherwise be hidden by the per-category
                  // truncation, render the full list for this category so the user
                  // can see (and uncheck) it. Bounded to one category at a time.
                  const visibleApps = hasSelectedHiddenApp ? matchingApps : truncatedApps
                  const hiddenAppCount = matchingApps.length - visibleApps.length
                  // While searching, hide categories with no matches so the
                  // results list reads as a flat filtered view. Otherwise we
                  // always show every category row so toggling categories on
                  // an empty search stays predictable.
                  if (isSearching && matchingApps.length === 0) return null
                  const hasSelectedApp = categoryApps.some((a) => appKeys.includes(a.app_key))
                  const showSubRows = categoryChecked || isSearching || hasSelectedApp
                  return (
                    <div key={categoryKey}>
                      <label
                        className={cn(
                          'hover:bg-accent flex w-full cursor-pointer items-center justify-between gap-2 px-3 py-2.5 text-sm font-semibold',
                          categoryChecked && 'bg-muted'
                        )}
                      >
                        <span>{label}</span>
                        <Checkbox
                          checked={categoryChecked}
                          onCheckedChange={() => toggleCategory(categoryKey)}
                          aria-label={`Toggle ${label}`}
                        />
                      </label>
                      {showSubRows && visibleApps.length > 0 && (
                        <div className="pb-1">
                          {visibleApps.map((app) => {
                            const appChecked = appKeys.includes(app.app_key)
                            return (
                              <label
                                key={app.app_key}
                                className="hover:bg-accent flex w-full cursor-pointer items-center justify-between gap-2 py-2 pl-9 pr-3 text-sm"
                              >
                                <span className="truncate">{app.display_name}</span>
                                <Checkbox
                                  checked={appChecked}
                                  onCheckedChange={() => toggleApp(app.app_key)}
                                  aria-label={`Toggle ${app.display_name}`}
                                />
                              </label>
                            )
                          })}
                          {hiddenAppCount > 0 && (
                            <div className="text-muted-foreground py-1.5 pl-9 pr-3 text-xs">+{hiddenAppCount} more</div>
                          )}
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}

export default AppFilter
